import { Newspaper, Play } from 'lucide-react';
import type { Product } from './types';

export const digitalNewsroomPlatform: Product = {
  id: 'digital-newsroom-platform',
  title: 'Complete Digital Newsroom Platform',
  subtitle: 'AI-Powered Journalism, Editorial Workflow & Multi-Channel Publishing Suite',
  description: 'Revolutionary digital newsroom platform combining AI-powered content creation, comprehensive editorial workflows, video production capabilities, and multi-channel publishing for modern news organizations, broadcasters, and digital publishers.',
  icon: Newspaper,
  image: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  category: 'Media & Publishing',
  status: 'Live',
  businessTypes: ['#media', '#publishing', '#journalism', '#broadcast', '#digital-news'],
  features: [
    // Core Publishing & Content Management (8 features)
    'AI-Powered Story Editor with Smart Suggestions & SEO Optimization',
    'Multi-Format Content Publishing (Text, Video, Audio, Images)',
    'Real-Time Collaborative Writing & Live Co-Editing',
    'Version Control & Complete Content History Tracking',
    'Content Scheduling & Embargo Management System',
    'Multi-Language Publishing with AI Translation Support',
    'Automated Content Archiving & Intelligent Retrieval',
    'Rich Media Embedding & Advanced Formatting Tools',
    
    // Editorial Workflow & Approval (8 features)
    'Configurable Multi-Stage Editorial Workflows',
    'Role-Based Access Control (Reporters, Editors, Publishers, Administrators)',
    'Deadline Tracking & Intelligent Task Assignment',
    'Interactive Editorial Calendar & Planning Tools',
    'Story Assignment & Beat Management System',
    'Fact-Checking Integration & Verification Workflow',
    'Legal & Compliance Review Integration',
    'Collaborative Story Commenting & Real-Time Feedback',
    
    // Video & Multimedia Production (7 features)
    'Video Upload, Encoding & Adaptive Streaming (HLS/DASH)',
    'AI-Powered Video Transcription & Auto-Captioning',
    'Image Gallery Management & Auto-Optimization',
    'Podcast & Audio Content Publishing Platform',
    'Live Streaming Integration (RTMP/HLS/WebRTC)',
    'Multimedia Asset Library with AI-Powered Tagging',
    'Automated Video Thumbnail Generation',
    
    // News App & Frontend (7 features)
    'Native iOS & Android News App Templates',
    'Progressive Web App (PWA) for Desktop & Mobile',
    'Intelligent Push Notification Management System',
    'Personalized News Feed with ML Algorithms',
    'Breaking News Alert System with Priority Publishing',
    'Offline Reading Mode with Background Sync',
    'Reader Engagement & Social Sharing Tools',
    
    // Advanced AI & Automation (8 features)
    'AI-Generated Headlines, Summaries & Article Suggestions',
    'Automated Content Tagging & Intelligent Categorization',
    'Plagiarism Detection & Originality Checking',
    'Sentiment Analysis for Reader Comments & Feedback',
    'AI-Powered Content Recommendations Engine',
    'Automated Social Media Publishing & Scheduling',
    'Trend Detection & Real-Time Story Suggestions',
    'Natural Language Search & Content Discovery',
    
    // Analytics & Monetization (6 features)
    'Real-Time Traffic Analytics Dashboard',
    'Subscription & Paywall Management System',
    'Advertising Integration (Programmatic & Direct Sales)',
    'Revenue Attribution & Performance Tracking',
    'Audience Segmentation & Demographics Analysis',
    'A/B Testing for Headlines, Layouts & Content',
    
    // Enterprise Security (8 features)
    'End-to-End Encryption for Source Protection',
    'Secure Document Drop (Whistleblower Protection System)',
    'Two-Factor Authentication & SSO Integration',
    'Comprehensive Audit Trails & Access Logging',
    'GDPR & Data Protection Compliance',
    'Secure Cloud Infrastructure (AWS/Azure/GCP)',
    'DDoS Protection & Global CDN Integration',
    'Role-Based Permission Management & Access Controls'
  ],
  pricing: { setup: 60600, monthly: 3800 },
  color: 'from-blue-600 to-indigo-700',
  demoConfig: {
    type: 'interactive',
    url: '/digital-newsroom/demo',
    title: 'Experience the Complete Platform',
    description: 'Explore our AI-powered digital newsroom with editorial workflows, video production, and multi-channel publishing',
    buttonText: 'Launch Newsroom Demo',
    icon: Play,
    buttonColor: 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
  }
};
